export default class RutUtils {
  static clean(rut) {
    return typeof rut === 'string' ? rut.replace(/[^0-9kK]+/g, '').toUpperCase() : '';
  }

  // Country-agnostic: strips only separators (dots, dashes, whitespace) and
  // uppercases, so a Chilean RUT ends up as stored ("123456789") while a DNI or
  // CURP keeps its letters. Mirrors the backend's RutUtils.normalizeIdentifier.
  static normalizeIdentifier(identifier) {
    if (identifier === null || identifier === undefined) return '';
    return String(identifier).replace(/[.\s-]/g, '').toUpperCase();
  }

  static formatRut(rut) {
    if (!rut) return '';
    var value = rut.toString();
    value = RutUtils.clean(value);
    let rutAux = value;
    var result =
      value.length > 1 ? rutAux.slice(-4, -1) + '-' + rutAux.substr(rutAux.length - 1) : value;
    for (var i = 4; i < rutAux.length; i += 3) {
      result = rutAux.slice(-3 - i, -i) + '.' + result;
    }
    return result;
  }

  static formatSerialNumber(serialNumber) {
    if (!serialNumber) return '';
    serialNumber = RutUtils.clean(serialNumber);
    var ret = [];
    var i;
    var len;

    for (i = 0, len = serialNumber.length; i < len; i += 3) {
      ret.push(serialNumber.substr(i, 3));
    }

    return ret.join('.');
  }

  static validateRut(rut) {
    if (!rut) {
      return true;
    }
    if (!/^0*(\d{1,3}(\.?\d{3})*)-?([\dkK])$/.test(rut)) {
      return false;
    }
    rut = RutUtils.clean(rut);
    var sinDv = parseInt(rut.slice(0, -1), 10);
    var m = 0;
    var s = 1;
    while (sinDv > 0) {
      s = (s + (sinDv % 10) * (9 - (m++ % 6))) % 11;
      sinDv = Math.floor(sinDv / 10);
    }
    const dv = s > 0 ? '' + (s - 1) : 'K';
    return dv === rut.slice(-1);
  }

  // RUT only applies to Chile. Other countries (e.g. Peru uses DNI/RUC)
  // must not be subject to Chilean RUT format/check-digit validation.
  // `country` may arrive as the country name ('Chile', 'Perú', possibly flag-prefixed)
  // from the 360 endpoint, or as the ISO code ('CL', 'PE') from the raw user object.
  // Returns true ONLY when the value positively identifies Chile; any other value
  // (Peru, unknown, '-', null/undefined) means RUT validation must be skipped.
  static usesRut(country) {
    if (typeof country !== 'string') return false;
    const c = country.trim().toLowerCase();
    return c === 'cl' || c.includes('chile');
  }

  // Label of a user's national identifier: RUT for Chile, DNI for any other known
  // country. A missing country keeps RUT, the label shown before the country was passed in.
  static identifierLabel(country) {
    if (!country) return 'RUT';
    return RutUtils.usesRut(country) ? 'RUT' : 'DNI';
  }

  // Display a user's national identifier: Chilean RUTs get the dotted format,
  // any other country's (e.g. a Mexican CURP) is shown as stored. An unknown
  // country keeps the Chilean format, which is what callers got before.
  static formatIdentifier(identifier, country) {
    if (!identifier) return '';
    if (!country || RutUtils.usesRut(country)) return RutUtils.formatRut(identifier);
    return identifier.toString();
  }

  // Clinic RUT/RUC in its country's usual written form: a Chilean RUT with dots and
  // dash (77.737.290-4); a Peruvian RUC (or any other country's id) as its plain
  // digits (20609690021), as SUNAT writes it. Without a country, an 11-digit value
  // is taken as a RUC. Stored and searched always without separators.
  static formatClinicTaxId(taxId, country) {
    const normalized = RutUtils.normalizeIdentifier(taxId);
    if (!normalized) return '';
    if (country) return RutUtils.usesRut(country) ? RutUtils.formatRut(normalized) : normalized;
    return /^\d{11}$/.test(normalized) ? normalized : RutUtils.formatRut(normalized);
  }

  // Chilean RUT character whitelist (the only RUT validation left): the value may
  // contain digits, the auto-inserted dots/dash, and an uppercase 'K' verifier only.
  // No check digit and no strict grouping are enforced. Lowercase 'k' is rejected
  // (the input handler already uppercases it via clean()).
  static validateRutChars(rut) {
    if (!rut) {
      return true;
    }
    return /^[0-9.\-K]+$/.test(rut);
  }

  static validateSerialNumber(serialNumber) {
    if (!serialNumber) {
      return true;
    }
    if (!/^[0-9]*\.?[0-9]*\.?[0-9]*$/.test(serialNumber)) {
      return false;
    }
    serialNumber = RutUtils.clean(serialNumber);
    return serialNumber.length === 9;
  }
}
